/**
 * Where a game's stat lands against a line: one rule for the model, the AI prompt and every chart.
 * A value equal to a standard over/under line is a push, neither an over nor an under, as settlement
 * voids it. An alternate "X+" line has no push: a value equal to it is an over, as settlement grades it.
 */

import { isAlternateMarket, type OverUnder } from './marketKey';

export const LINE_OUTCOME = { OVER: 'over', UNDER: 'under', PUSH: 'push' } as const;
export type LineOutcome = (typeof LINE_OUTCOME)[keyof typeof LINE_OUTCOME];

export const BET_OUTCOME = { HIT: 'hit', MISS: 'miss', PUSH: 'push' } as const;
export type BetOutcome = (typeof BET_OUTCOME)[keyof typeof BET_OUTCOME];

export function lineOutcome(value: number, line: number, isAlternateLine: boolean): LineOutcome {
  if (value > line) return LINE_OUTCOME.OVER;
  if (value < line) return LINE_OUTCOME.UNDER;
  return isAlternateLine ? LINE_OUTCOME.OVER : LINE_OUTCOME.PUSH;
}

/** A bet on `side` of the line: hit, miss, or push when the value lands on a standard line. */
export function betOutcome(
  value: number,
  line: number,
  side: OverUnder,
  isAlternateLine: boolean,
): BetOutcome {
  const outcome = lineOutcome(value, line, isAlternateLine);
  if (outcome === LINE_OUTCOME.PUSH) return BET_OUTCOME.PUSH;
  return outcome === side ? BET_OUTCOME.HIT : BET_OUTCOME.MISS;
}

/**
 * A team chart game by its market's grader: a spread is a push when the margin lands on the line and a
 * game total when the total does, alternate or not, as gradeOutcome grades them; a moneyline reads
 * `won`, since a basketball game goes to overtime rather than end tied.
 */
export function teamLineOutcome(
  point: { value: number; won: boolean },
  propType: string,
  propLine: number,
  isUnderBet: boolean,
): BetOutcome {
  if (propType === 'spread') return betOutcome(point.value, -propLine, 'over', false);
  if (propType === 'total') {
    return betOutcome(point.value, propLine, isUnderBet ? 'under' : 'over', false);
  }
  return point.won ? BET_OUTCOME.HIT : BET_OUTCOME.MISS;
}

/** The newest decided outcome and how many in a row share it, newest first; a push is skipped. */
function decidedRun<T extends string>(
  outcomesNewestFirst: readonly T[],
  push: T,
): { outcome: T; length: number } | null {
  const decided = outcomesNewestFirst.filter((outcome) => outcome !== push);
  if (decided.length === 0) return null;
  const outcome = decided[0];
  const length = decided.findIndex((o) => o !== outcome);
  return { outcome, length: length === -1 ? decided.length : length };
}

/** Overs (+) or unders (-) in a row, newest first, through any push; null when nothing is decided. */
export function overUnderStreak(outcomesNewestFirst: readonly LineOutcome[]): number | null {
  const run = decidedRun(outcomesNewestFirst, LINE_OUTCOME.PUSH);
  if (!run) return null;
  return run.outcome === LINE_OUTCOME.OVER ? run.length : -run.length;
}

/** Hits or misses in a row, newest first, through any push; null when nothing is decided. */
export function betStreak(
  outcomesNewestFirst: readonly BetOutcome[],
): { outcome: typeof BET_OUTCOME.HIT | typeof BET_OUTCOME.MISS; length: number } | null {
  const run = decidedRun(outcomesNewestFirst, BET_OUTCOME.PUSH);
  if (!run || run.outcome === BET_OUTCOME.PUSH) return null;
  return { outcome: run.outcome, length: run.length };
}

/** Hits among decided games; a push is in neither count. */
export function hitSummary(outcomes: readonly BetOutcome[]): {
  hits: number;
  decided: number;
  pushes: number;
} {
  const pushes = outcomes.filter((o) => o === BET_OUTCOME.PUSH).length;
  const hits = outcomes.filter((o) => o === BET_OUTCOME.HIT).length;
  return { hits, decided: outcomes.length - pushes, pushes };
}

/** A line on one stat: the leg's own, or what a chart shows. */
export interface StatLine {
  line: number | null | undefined;
  statKey: string | null | undefined;
}

/**
 * Whether a chart's line is its leg's alternate "X+" line: the leg is an alternate market and the
 * chart still shows the leg's own stat and line. A line the user drags or types, or another stat,
 * is a plain over/under line.
 */
export function isAlternateChartLine(
  marketKey: string | null | undefined,
  leg: StatLine,
  shown: StatLine,
): boolean {
  return (
    isAlternateMarket(marketKey) &&
    leg.line != null &&
    shown.line === leg.line &&
    shown.statKey === leg.statKey
  );
}
